import { Search, Route, Shovel, RadioTower, GitMerge, FileCheck2, Wrench, ArrowRight } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";

const STEPS = [
  { icon: Search, title: "Route Survey", desc: "Site inspection, route identification and feasibility assessment." },
  { icon: Route, title: "Route Preparation", desc: "Planning and preparation of the cable route." },
  { icon: Shovel, title: "Underground OFC Laying", desc: "Trenching, ducting and cable laying activities." },
  { icon: RadioTower, title: "Aerial OFC Installation", desc: "Pole-based and aerial optical fiber deployment." },
  { icon: GitMerge, title: "Fiber Splicing", desc: "Professional fiber jointing and termination." },
  { icon: FileCheck2, title: "Testing & Commissioning", desc: "OTDR testing, link testing and final commissioning." },
  { icon: Wrench, title: "Maintenance & Support", desc: "Ongoing infrastructure support and maintenance." },
];

export default function Process({ onQuote }) {
  return (
    <section
      id="infrastructure"
      data-testid="process-section"
      className="relative overflow-hidden bg-navy py-24 lg:py-32"
    >
      <div className="jtk-dotgrid pointer-events-none absolute inset-0 opacity-60" />
      <div className="pointer-events-none absolute -left-40 top-0 h-[420px] w-[420px] rounded-full bg-electric/15 blur-[140px]" />
      <div className="pointer-events-none absolute -right-32 bottom-0 h-[380px] w-[380px] rounded-full bg-cyan-500/10 blur-[130px]" />

      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <div className="max-w-3xl">
          <SectionHead
            dark
            eyebrow="Civil & OFC Works"
            title="From Groundwork to Connectivity."
            sub="Specialized civil and optical fiber infrastructure works — a disciplined seven-step delivery process for underground and aerial OFC deployment."
          />
        </div>

        <div className="relative mt-16 max-w-3xl">
          <span className="absolute left-[27px] top-2 bottom-2 w-px bg-gradient-to-b from-cyan-400/70 via-blue-500/50 to-transparent sm:left-[31px]" />
          {STEPS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.06}>
              <div data-testid={`process-step-${i + 1}`} className="group relative flex gap-6 pb-10 last:pb-0 sm:gap-8">
                <div className="relative z-10 flex flex-col items-center">
                  <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-navy-800 text-cyan-300 shadow-lg shadow-navy-900 transition-all duration-300 group-hover:border-cyan-400/40 group-hover:shadow-[0_0_24px_rgba(6,182,212,0.35)] sm:h-16 sm:w-16">
                    <s.icon size={22} />
                  </span>
                </div>
                <div className="rounded-2xl border border-white/[0.07] bg-white/[0.03] p-5 backdrop-blur-sm transition-all duration-300 group-hover:border-cyan-400/25 group-hover:bg-white/[0.06] sm:p-6 flex-1">
                  <p className="font-mono text-[11px] font-semibold tracking-[0.3em] text-cyan-400">
                    STEP {String(i + 1).padStart(2, "0")}
                  </p>
                  <h3 className="mt-2 font-heading text-lg font-bold text-white sm:text-xl">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <button
            data-testid="process-discuss-button"
            onClick={() => onQuote("Underground / Aerial OFC Laying")}
            className="group mt-14 inline-flex items-center gap-2.5 rounded-full bg-electric px-7 py-3.5 text-sm font-semibold text-white shadow-xl shadow-blue-600/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-blue-500"
          >
            Discuss Your OFC Deployment Requirement
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </Reveal>
      </div>
    </section>
  );
}
